import React, { memo, useMemo } from "react";
import {
  Container,
  PageButton,
  PageNumber,
  Ellipsis,
  RangeText,
  Controls,
} from "./styles";

/**
 * Calcula los números de página a mostrar.
 * Si totalPages ≤ 7 → muestra todas: [1, 2, 3, 4, 5].
 * Si totalPages > 7 con elipsis:
 *   - cerca del inicio:    [1, 2, 3, 4, 5, ..., last]
 *   - en el medio:         [1, ..., p-1, p, p+1, ..., last]
 *   - cerca del final:     [1, ..., last-4, last-3, last-2, last-1, last]
 */
const buildPageList = (current, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = [];
  const showLeftEllipsis = current > 4;
  const showRightEllipsis = current < totalPages - 3;

  pages.push(1);

  if (showLeftEllipsis) pages.push("…");

  const start = showLeftEllipsis ? current - 1 : 2;
  const end = showRightEllipsis ? current + 1 : totalPages - 1;
  for (let i = start; i <= end; i++) pages.push(i);

  if (showRightEllipsis) pages.push("…");

  pages.push(totalPages);

  return pages;
};

const Pagination = memo(({ currentPage, totalItems, pageSize, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const pageList = useMemo(
    () => buildPageList(currentPage, totalPages),
    [currentPage, totalPages]
  );

  // Si todo entra en una página, no mostramos controles — solo el contador (opcional).
  if (totalPages <= 1) {
    return (
      <Container>
        <RangeText>
          {totalItems === 0
            ? "Sin resultados"
            : `Mostrando ${totalItems} ${totalItems === 1 ? "ítem" : "ítems"}`}
        </RangeText>
      </Container>
    );
  }

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  const goTo = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
  };

  return (
    <Container>
      <Controls>
        <PageButton
          onClick={() => goTo(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Página anterior"
        >
          ← Anterior
        </PageButton>

        {pageList.map((p, idx) =>
          p === "…" ? (
            <Ellipsis key={`ell-${idx}`}>…</Ellipsis>
          ) : (
            <PageNumber
              key={p}
              active={p === currentPage}
              onClick={() => goTo(p)}
              aria-label={`Ir a página ${p}`}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </PageNumber>
          )
        )}

        <PageButton
          onClick={() => goTo(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Página siguiente"
        >
          Siguiente →
        </PageButton>
      </Controls>

      <RangeText>
        Mostrando {start}-{end} de {totalItems}
      </RangeText>
    </Container>
  );
});

export default Pagination;
